import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type {
  AdminContact,
  ScheduledEvent,
  SmsAudience,
  SmsBroadcastTarget,
  SmsBudgetPublic,
  SmsCampaign,
  SmsMessageLogEntry,
  SmsSettingsPublic,
} from "@tih/shared";
import {
  ApiError,
  adminFetchContactsBook,
  adminFetchEvents,
  adminFetchSettingsSms,
  adminFetchSmsBudget,
  adminFetchSmsCampaigns,
  adminFetchSmsMessages,
  adminPreviewSmsRecipients,
  adminRetrySmsMessage,
  adminSendSmsBroadcast,
} from "../../api/client";
import { AddressBookPanel } from "../../components/admin/AddressBookPanel";

type Tab = "compose" | "book" | "history" | "messages";
type TargetKind = SmsBroadcastTarget["kind"];

const EVENT_AUDIENCES: { value: SmsAudience; label: string }[] = [
  { value: "checked_in", label: "Checked in" },
  { value: "attendees", label: "Everyone on the door list" },
  { value: "reservations", label: "Reservations" },
  { value: "all_with_phones", label: "Attendees and reservations" },
];

/** GSM-7 messages hold 160 chars; anything outside it drops to 70-char UCS-2 parts. */
function smsSegments(message: string): { unicode: boolean; parts: number; perPart: number } {
  const unicode = [...message].some((ch) => ch.codePointAt(0)! > 127);
  const single = unicode ? 70 : 160;
  const multi = unicode ? 67 : 153;
  if (message.length === 0) return { unicode, parts: 0, perPart: single };
  if (message.length <= single) return { unicode, parts: 1, perPart: single };
  return { unicode, parts: Math.ceil(message.length / multi), perPart: multi };
}

function eventLabel(event: ScheduledEvent): string {
  const when = new Date(event.startsAt).toLocaleDateString(undefined, {
    dateStyle: "medium",
  });
  return event.title?.trim() ? `${event.title} - ${when}` : when;
}

export function AdminCommunicationPage() {
  const [tab, setTab] = useState<Tab>("compose");
  const [contacts, setContacts] = useState<AdminContact[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [events, setEvents] = useState<ScheduledEvent[]>([]);
  const [sms, setSms] = useState<SmsSettingsPublic | null>(null);
  const [campaigns, setCampaigns] = useState<SmsCampaign[]>([]);
  const [smsBudget, setSmsBudget] = useState<SmsBudgetPublic | null>(null);
  const [messageLog, setMessageLog] = useState<SmsMessageLogEntry[]>([]);
  const [msgStatusFilter, setMsgStatusFilter] = useState<"" | "SENT" | "FAILED" | "PENDING">("");
  const [msgFrom, setMsgFrom] = useState("");
  const [msgTo, setMsgTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [retryBusyId, setRetryBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Compose
  const [targetKind, setTargetKind] = useState<TargetKind>("contacts");
  const [targetTags, setTargetTags] = useState<string[]>([]);
  const [targetEventId, setTargetEventId] = useState<number | null>(null);
  const [targetAudience, setTargetAudience] = useState<SmsAudience>("checked_in");
  const [manualNumbers, setManualNumbers] = useState("");
  const [message, setMessage] = useState("");
  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const [counting, setCounting] = useState(false);
  const segments = useMemo(() => smsSegments(message), [message]);

  // Address book
  const [selected, setSelected] = useState<Set<number>>(new Set());

  async function loadContacts() {
    const payload = await adminFetchContactsBook();
    setContacts(payload.contacts);
    setAllTags(payload.tags);
  }

  async function loadAll() {
    setLoading(true);
    setError(null);
    try {
      const [, eventRows, smsSettings, budgetPayload] = await Promise.all([
        loadContacts(),
        adminFetchEvents().catch(() => [] as ScheduledEvent[]),
        adminFetchSettingsSms().catch(() => null),
        adminFetchSmsBudget().catch(() => null),
      ]);
      setEvents(eventRows);
      setSms(smsSettings);
      setSmsBudget(budgetPayload?.budget ?? null);
      if (eventRows.length > 0) {
        const upcoming =
          eventRows.find((e) => new Date(e.endsAt).getTime() >= Date.now()) ?? eventRows[0];
        setTargetEventId((prev) => prev ?? upcoming.id);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load communication data");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadAll();
  }, []);

  useEffect(() => {
    if (tab !== "history") return;
    void adminFetchSmsCampaigns()
      .then((res) => setCampaigns(res.campaigns))
      .catch(() => setCampaigns([]));
  }, [tab]);

  useEffect(() => {
    if (tab !== "messages") return;
    void adminFetchSmsMessages({
      ...(msgStatusFilter ? { status: msgStatusFilter } : {}),
      ...(msgFrom ? { from: new Date(msgFrom).toISOString() } : {}),
      ...(msgTo ? { to: new Date(msgTo + "T23:59:59").toISOString() } : {}),
    })
      .then((res) => setMessageLog(res.messages))
      .catch(() => setMessageLog([]));
  }, [tab, msgStatusFilter, msgFrom, msgTo]);

  const selectedIds = useMemo(() => [...selected], [selected]);

  const target = useMemo<SmsBroadcastTarget | null>(() => {
    switch (targetKind) {
      case "contacts":
        return { kind: "contacts" };
      case "tags":
        return targetTags.length > 0 ? { kind: "tags", tags: targetTags } : null;
      case "selected":
        return selectedIds.length > 0 ? { kind: "selected", ids: selectedIds } : null;
      case "event":
        return targetEventId != null
          ? { kind: "event", eventId: targetEventId, audience: targetAudience }
          : null;
      case "manual": {
        const numbers = manualNumbers
          .split(/[\n,;]/)
          .map((n) => n.trim())
          .filter(Boolean);
        return numbers.length > 0 ? { kind: "manual", numbers } : null;
      }
    }
  }, [targetKind, targetTags, selectedIds, targetEventId, targetAudience, manualNumbers]);

  // Ask the server how many numbers the current target actually resolves to.
  const targetKey = target ? JSON.stringify(target) : "";
  useEffect(() => {
    if (!targetKey) {
      setRecipientCount(null);
      return;
    }
    let cancelled = false;
    setCounting(true);
    const timer = setTimeout(() => {
      adminPreviewSmsRecipients(JSON.parse(targetKey) as SmsBroadcastTarget)
        .then((res) => {
          if (!cancelled) setRecipientCount(res.recipients);
        })
        .catch(() => {
          if (!cancelled) setRecipientCount(null);
        })
        .finally(() => {
          if (!cancelled) setCounting(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [targetKey]);

  async function send() {
    if (!target || !message.trim()) return;
    const count = recipientCount ?? 0;
    if (!confirm(`Send this SMS to ${count} number${count === 1 ? "" : "s"}?`)) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await adminSendSmsBroadcast({ target, message: message.trim() });
      const failed = result.failed > 0 ? ` ${result.failed} failed.` : "";
      const cost =
        result.costUgx != null ? ` Cost UGX ${result.costUgx.toLocaleString()}.` : "";
      setSuccess(`SMS sent to ${result.sent} of ${result.recipients}.${failed}${cost}`);
      const budget = await adminFetchSmsBudget().catch(() => null);
      if (budget) setSmsBudget(budget.budget);
      setMessage("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send SMS");
    } finally {
      setBusy(false);
    }
  }

  const subscribedCount = contacts.filter((c) => c.subscribed).length;
  const costPerSms = smsBudget?.costPerSmsUgx ?? 25;
  const estimateUgx =
    recipientCount != null && recipientCount > 0 ? recipientCount * costPerSms : null;
  const overBudget =
    estimateUgx != null && smsBudget != null && smsBudget.balanceUgx < estimateUgx;


  async function reloadMessageLog() {
    const res = await adminFetchSmsMessages({
      ...(msgStatusFilter ? { status: msgStatusFilter } : {}),
      ...(msgFrom ? { from: new Date(msgFrom).toISOString() } : {}),
      ...(msgTo ? { to: new Date(msgTo + "T23:59:59").toISOString() } : {}),
    });
    setMessageLog(res.messages);
  }

  async function retryFailed(id: number) {
    if (!confirm("Retry this failed SMS to the same number?")) return;
    setRetryBusyId(id);
    setError(null);
    setSuccess(null);
    try {
      const result = await adminRetrySmsMessage(id);
      setSuccess(
        result.sent > 0
          ? `Retry sent to ${result.message.toPhone}.`
          : `Retry failed for ${result.message.toPhone}${result.message.error ? `: ${result.message.error}` : "."}`,
      );
      const budget = await adminFetchSmsBudget().catch(() => null);
      if (budget) setSmsBudget(budget.budget);
      await reloadMessageLog();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not retry SMS");
    } finally {
      setRetryBusyId(null);
    }
  }

  const canSend = Boolean(
    sms?.configured &&
      target &&
      message.trim() &&
      (recipientCount ?? 0) > 0 &&
      !overBudget,
  );

  return (
    <div className="admin-page">
      <header className="admin-page-header admin-page-header-row">
        <div>
          <h1>Communication</h1>
          <p>
            Draft an SMS and send it to your whole address book, a tag, or an event
            audience through Africa&apos;s Talking. {contacts.length} contact
            {contacts.length === 1 ? "" : "s"} on file, {subscribedCount} subscribed.
          </p>
        </div>
      </header>

      {!sms?.configured && (
        <div className="admin-event-context-banner" style={{ marginBottom: 16 }}>
          SMS is not configured — add your Africa&apos;s Talking username and API key in{" "}
          <Link to="/admin/settings/sms">Settings → SMS</Link>. You can still manage the
          address book.
        </div>
      )}

      {smsBudget && (
        <div className="admin-event-context-banner" style={{ marginBottom: 16 }}>
          SMS budget: <strong>UGX {smsBudget.balanceUgx.toLocaleString()}</strong>
          {" · "}
          UGX {smsBudget.costPerSmsUgx} per successful send.
          {" "}
          <Link to="/admin/settings/sms">Top up / Airtel Money instructions</Link>
        </div>
      )}

      {error && <div className="form-error">{error}</div>}
      {success && (
        <div className="form-success" style={{ padding: 12, marginBottom: 16 }}>
          {success}
        </div>
      )}

      <nav className="admin-tabs" aria-label="Communication sections">
        <button
          type="button"
          className={tab === "compose" ? "active" : undefined}
          onClick={() => setTab("compose")}
        >
          Compose
        </button>
        <button
          type="button"
          className={tab === "book" ? "active" : undefined}
          onClick={() => setTab("book")}
        >
          Address book ({contacts.length})
        </button>
        <button
          type="button"
          className={tab === "history" ? "active" : undefined}
          onClick={() => setTab("history")}
        >
          History
        </button>
        <button
          type="button"
          className={tab === "messages" ? "active" : undefined}
          onClick={() => setTab("messages")}
        >
          Message log
        </button>
      </nav>

      {loading ? (
        <div style={{ opacity: 0.5 }}>Loading…</div>
      ) : tab === "compose" ? (
        <section className="admin-card">
          <h2 className="admin-card-title">Draft a message</h2>
          <div className="admin-blog-form">
            <div className="admin-blog-form-fields">
              <label>
                <span>Send to</span>
                <select
                  value={targetKind}
                  disabled={busy}
                  onChange={(e) => setTargetKind(e.target.value as TargetKind)}
                >
                  <option value="contacts">Everyone in the address book</option>
                  <option value="tags">Contacts with a tag</option>
                  <option value="selected">
                    Contacts picked in the address book ({selectedIds.length})
                  </option>
                  <option value="event">An event audience</option>
                  <option value="manual">Numbers I type in</option>
                </select>
              </label>

              {targetKind === "tags" && (
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  {allTags.length === 0 && (
                    <p className="admin-muted">
                      No tags yet — add tags to contacts in the address book.
                    </p>
                  )}
                  {allTags.map((tag) => (
                    <label
                      key={tag}
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <input
                        type="checkbox"
                        checked={targetTags.includes(tag)}
                        disabled={busy}
                        onChange={() =>
                          setTargetTags((prev) =>
                            prev.includes(tag)
                              ? prev.filter((t) => t !== tag)
                              : [...prev, tag],
                          )
                        }
                      />
                      <span>{tag}</span>
                    </label>
                  ))}
                </div>
              )}

              {targetKind === "event" && (
                <>
                  <label>
                    <span>Event</span>
                    <select
                      value={targetEventId ?? ""}
                      disabled={busy}
                      onChange={(e) => setTargetEventId(Number(e.target.value) || null)}
                    >
                      <option value="">Choose an event…</option>
                      {events.map((event) => (
                        <option key={event.id} value={event.id}>
                          {eventLabel(event)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>Who at that event</span>
                    <select
                      value={targetAudience}
                      disabled={busy}
                      onChange={(e) => setTargetAudience(e.target.value as SmsAudience)}
                    >
                      {EVENT_AUDIENCES.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              )}

              {targetKind === "manual" && (
                <label>
                  <span>Numbers (one per line, or comma separated)</span>
                  <textarea
                    rows={4}
                    value={manualNumbers}
                    disabled={busy}
                    onChange={(e) => setManualNumbers(e.target.value)}
                    placeholder={"0700123456\n+256700654321"}
                  />
                </label>
              )}

              <p className="admin-muted">
                {counting
                  ? "Counting recipients…"
                  : recipientCount == null
                    ? "Choose a group to see how many will be texted."
                    : `${recipientCount} number${
                        recipientCount === 1 ? "" : "s"
                      } ready to send. Opted-out contacts are excluded.`}
              </p>
              {estimateUgx != null && (
                <p className={overBudget ? "form-error" : "admin-muted"}>
                  This send will cost ~UGX {estimateUgx.toLocaleString()} (
                  {recipientCount} × {costPerSms})
                  {smsBudget
                    ? ` · balance UGX ${smsBudget.balanceUgx.toLocaleString()}`
                    : ""}
                  {overBudget ? ". Top up SMS budget before sending." : "."}
                </p>
              )}

              <label>
                <span>
                  Message ({message.length}/1000 — {segments.parts} SMS part
                  {segments.parts === 1 ? "" : "s"}
                  {segments.unicode ? ", unicode" : ""})
                </span>
                <textarea
                  rows={6}
                  maxLength={1000}
                  value={message}
                  disabled={busy}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Together In Heaven — doors open at 8pm tonight. We look forward to worshipping with you."
                />
              </label>
            </div>
            <div className="admin-blog-form-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy || !canSend}
                onClick={() => void send()}
              >
                {busy ? "Sending…" : `Send SMS${recipientCount ? ` (${recipientCount})` : ""}`}
              </button>
            </div>
          </div>
        </section>
      ) : tab === "book" ? (
        <>
          <p className="admin-card-sub" style={{ marginBottom: 16 }}>
            Full People address book — including sync and attendance — also lives at{" "}
            <Link to="/admin/people/address-book">/admin/people/address-book</Link>.
            Selections here feed the compose tab&apos;s &quot;picked contacts&quot; target.
          </p>
          <AddressBookPanel
            embedded
            selectable
            selectedIds={[...selected]}
            onSelectedChange={(ids) => setSelected(new Set(ids))}
          />
        </>
      ) : tab === "history" ? (
        campaigns.length === 0 ? (
          <div style={{ opacity: 0.6 }}>Nothing sent yet.</div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Sent</th>
                  <th>Audience</th>
                  <th>Message</th>
                  <th>Delivered</th>
                  <th>Cost</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((row) => (
                  <tr key={row.id}>
                    <td>{new Date(row.createdAt).toLocaleString()}</td>
                    <td>{row.audience}</td>
                    <td style={{ maxWidth: 360 }}>{row.message}</td>
                    <td>
                      {row.sent}/{row.recipients}
                      {row.failed > 0 && ` · ${row.failed} failed`}
                    </td>
                    <td>UGX {(row.costUgx ?? 0).toLocaleString()}</td>
                    <td>{row.sentByName ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <section className="admin-card">
          <h2 className="admin-card-title">Message log</h2>
          <p className="admin-card-sub">
            Per-recipient Africa&apos;s Talking results with phone, status, campaign, and time. Filter failed and retry (re-checks isValidSmsPhone / opt-out).
          </p>
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <select
              value={msgStatusFilter}
              onChange={(e) =>
                setMsgStatusFilter(e.target.value as "" | "SENT" | "FAILED" | "PENDING")
              }
            >
              <option value="">All statuses</option>
              <option value="SENT">Sent</option>
              <option value="FAILED">Failed</option>
              <option value="PENDING">Pending</option>
            </select>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span className="admin-muted">From</span>
              <input
                type="date"
                value={msgFrom}
                onChange={(e) => setMsgFrom(e.target.value)}
              />
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span className="admin-muted">To</span>
              <input type="date" value={msgTo} onChange={(e) => setMsgTo(e.target.value)} />
            </label>
          </div>
          {messageLog.length === 0 ? (
            <div style={{ opacity: 0.6 }}>No messages match.</div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>To</th>
                    <th>Status</th>
                    <th>Campaign</th>
                    <th>Cost</th>
                    <th>Message</th>
                    <th>Error</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {messageLog.map((row) => (
                    <tr key={row.id}>
                      <td>{new Date(row.createdAt).toLocaleString()}</td>
                      <td>{row.toPhone}</td>
                      <td>
                        {row.status}
                        {row.sandbox ? " · sandbox" : ""}
                      </td>
                      <td>{row.campaignId ?? "—"}</td>
                      <td>UGX {row.costUgx.toLocaleString()}</td>
                      <td style={{ maxWidth: 280 }}>{row.body}</td>
                      <td style={{ maxWidth: 200 }}>{row.error ?? "—"}</td>
                      <td>
                        {row.status === "FAILED" ? (
                          <button
                            type="button"
                            className="btn btn-ghost"
                            disabled={retryBusyId === row.id || !sms?.configured}
                            onClick={() => void retryFailed(row.id)}
                          >
                            {retryBusyId === row.id ? "Retrying…" : "Retry"}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
